<template>
  <div v-if="!firstRender && rows.length" class="MultiColumnTable" :style="{ overflow: scrollDisabled ? 'visible' : 'auto' }">
    <table ref="tableRef">
      <thead>
        <tr v-for="(columns, levelIndex) in columnsByLevel" style="height: 28px;">
          <th v-if="levelIndex == 0" class="tableTitle" :rowSpan="columnsByLevel.length" :colSpan="maxHeaderLength" >
            <template v-if="showingDesktopUi && showTableName">
              {{ props.tableName ?? table.tableName }}
            </template>
          </th>
          <th v-for="(column, columnIndex) in columns"
            :class="{ firstColumnHeader: columnIndex == 0 }"
            :style="{ width: column.width }"
            :colSpan="column.colSpan"
            @click="toggleShowingAllBins()"
          >
            <slot name="columnHeader" :column>
              <template v-if="columns.length > 1">
                {{ column.name }}
              </template>
            </slot>
          </th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="row in rows" :key="row.id">
          <th v-for="header in row.headers" 
            :rowspan="header.rowspan" 
            :colspan="header.colspan"
            :class="header.cssClasses"
          >
            {{ header.text }}
          </th>
          <template v-for="(cell, cellIndex) in row.cells">
            <template v-if="cell == null">
              <td></td>
            </template>
            <template v-else>
              <component class="relevant" v-bind="cell.componentArgs"
                :is="cell.componentType"
                :row="row"
                :cell="cell"
                :columnTag="cell.columnTag"
                :selectedCells="selectedCellsOrNull(row)"
                :showingPopover="isShowingPopover(row, cellIndex)"
                :applyToAllColumnsEnabled="cellApplyToAllColumnsEnabled(row, cell)"
                @update:showingPopover="value => setShowingPopover(row, cellIndex, value)"
                v-model:columnSelectionCode="columnSelectionCode"
              >
                <template #BinSelectionInput>
                  <BinSelectionInput
                    :tag="cell.componentArgs.tag"
                    :applyToAllColumnsEnabled="cellApplyToAllColumnsEnabled(row, cell)"
                    v-model:columnSelectionCode="columnSelectionCode"
                  ></BinSelectionInput>
                </template>
              </component>
            </template>
          </template>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script setup>
import { computed, ref, onMounted, watch } from "vue";
import { promiseTimeout } from "@vueuse/core";
import store, { showingDesktopUi } from "./store.js";
import { chunk, hasMatchingItemsBy, groupBy, map_dropNulls, sortBy, sumBy } from "./utility.js";
import { resolveTable, combineTables } from "./TableUtility.js";

let props = defineProps({
  tableName: {},
  tableData: {},
  tableKey: {},
  scrollDisabled: { type: Boolean, default: false },
  showTableName: { type: Boolean, default: true },
  showSystemColumnsWhenMerged: { type: Boolean, default: true },
  disableFirstRenderDelay: { type: Boolean, default: false }
});

let firstRender = ref(props.disableFirstRenderDelay ? false : true);
onMounted(async () => {
  await promiseTimeout(10);
  firstRender.value = false;
});

let table = computed(() => {
  if (props.tableData) {
    return props.tableData;
  }
  else {
    let connectedSessions = store.sessions.filter(session => session.isConnected);
    let tables = connectedSessions.map(session => {
      let tableData = session.system.tables[props.tableKey].value;
      let table = resolveTable(session, session.system, tableData);
      return table;
    });
    let combinedTable = combineTables(tables, props.showSystemColumnsWhenMerged);
    return combinedTable;
  }
});

let columnsByLevel = computed(() => {
  let columnsByLevel = [];
  let traversalQueue = [ ... table.value.columns ];
  while (traversalQueue.length > 0) {
    let levelSize = traversalQueue.length;
    let levelColumns = [];
    for (let i = 0; i < levelSize; i++) {
      let column = traversalQueue.shift();
      levelColumns.push(column);
      if (column.subColumns) {
        column.subColumns.forEach(subColumn => {
          traversalQueue.push(subColumn);
        });
      }
    }
    columnsByLevel.push(levelColumns);
  }
  return columnsByLevel;
});

let rows = computed(() => 
{
  // Rename the rows if necessary, and parse the headers.
  let rows = table.value.rows.map(row => {
    let rowName = row.rowName;
    let headersSplit = rowName.split(/(\|+)/);
    let headerChunks = chunk(headersSplit, 2);
    let headers = headerChunks.map(chunk => {
      return {
        text: chunk[0].trim(),
        forceSplit: chunk[1] == "||",
        comparisonValue: chunk.join()
      }
    })
    return {
      ... row,
      rowName,
      headers
    };
  });

  // Shorten row headers like the following
  //
  //    Auxiliary 1 | ?Temperature | Setting X
  //    Auxiliary 1 | ?Temperature | Setting Y
  //
  // into 
  //
  //    Auxiliary 1 | Setting X
  //    Auxiliary 1 | Setting Y
  //
  // if there are no other aggregates under "Auxiliary 1" in any single column.
  //
  rows.forEach(row => {
    row.headers = row.headers.filter((header, index) => {
      if (header.text.startsWith("?")) {
        let precedingHeaders = row.headers.slice(0, index);
        let rowStartsWith = (row, headers) => headers.every((header, i) => header.comparisonValue == row.headers[i].comparisonValue);
        let group = rows.filter(row => rowStartsWith(row, precedingHeaders));
        let groupHasAtLeastTwoRowsWithSameColumn = hasMatchingItemsBy(group, (row1, row2) => {
          let bothRowsHaveAtLeastOneSameColumn = row1.cells.some((cell, index) => cell != null && row2.cells[index] != null);
          return bothRowsHaveAtLeastOneSameColumn;
        });
        return groupHasAtLeastTwoRowsWithSameColumn;
      }
      else {
        return true;
      }
    });
  });

  // Shorten row headers like "Supply | Temperature" into "Supply temperature" when there are no other aggregates under "Supply".
  // Do not shorten row headers that use a doubled separator, as in "Supply || Temperature".
  rows.forEach(row => {
    for (let i = row.headers.length - 1; i >= 1; i--) {
      let header = row.headers[i];
      let precedingHeaders = row.headers.slice(0, i).map(header => header.comparisonValue).join();
      let hasSiblingRow = rows.find(other => {
        if (row == other) return false;
        if (header.comparisonValue == other.headers[i]?.comparisonValue) return false; // Preceding headers must match, but the header itself must not match.
        let other_precedingHeaders = other.headers.slice(0, i).map(header => header.comparisonValue).join();
        let bothRowsHaveSamePrecedingHeaders = (precedingHeaders == other_precedingHeaders);
        return bothRowsHaveSamePrecedingHeaders;
      });
      
      let precedingHeader = row.headers[i - 1];
      let canCombineHeaders = !hasSiblingRow && !precedingHeader.forceSplit;
      if (canCombineHeaders) {
        row.headers[i - 1].text += " " + header.text.toLowerCase();
        row.headers.pop();
      }
    }
  });

  // Reorder and group rows based on their header text.
  // For example
  //    Aux 1 | Setting X
  //    Aux 2 | Setting X
  //    Aux 1 | Setting Y
  //    Aux 2 | Setting Y
  // should become
  //    Aux 1 | Setting X
  //    Aux 1 | Setting Y
  //    Aux 2 | Setting X
  //    Aux 2 | Setting Y
  function groupRows(rows, headerColumnIndex) {
    if (rows.length == 0) return rows;
    if (rows[0].headers.length <= headerColumnIndex) return rows;
    let groups = groupBy(rows, row => row.headers[headerColumnIndex].comparisonValue);
    let headersValues = sortBy(Object.keys(groups), headerValue => {
      return rows.findIndex(row => row.headers[headerColumnIndex].comparisonValue == headerValue);
    });
    let _rows = [];
    headersValues.forEach(headerValue => {
      let group = groups[headerValue];
      _rows.push( ... groupRows(group, headerColumnIndex + 1));
    })
    return _rows;
  }
  rows = groupRows(rows, 0);

  // Convert headers to { text, rowspan, colspan }.
  let headerLengths = rows.map(row => row.headers.length);
  let maxHeaderLength = Math.max(... headerLengths);
  rows = rows.map((row, rowIndex) => {
    let prevRow = (rowIndex > 0) ? rows[rowIndex - 1] : null;
    return {
      ... row,
      headers: row.headers.map((header, headerIndex) => {
        let joinLeadingHeaders = (targetRow) => targetRow?.headers.slice(0, headerIndex + 1).map(header => header.comparisonValue).join();
        let isCoveredHeader = joinLeadingHeaders(prevRow) == joinLeadingHeaders(row);
        if (isCoveredHeader && showingDesktopUi.value) return null;
        let isLastHeader = (headerIndex == row.headers.length - 1);
        let childRows = rows.slice(rowIndex + 1).filter(other => joinLeadingHeaders(other) == joinLeadingHeaders(row));
        let rowspan = 1 + childRows.length;
        let colspan = (isLastHeader) ? maxHeaderLength - headerIndex : 1;
        return {
          text: header.text.replace("?", ""),
          rowspan,
          colspan
        };
      })
    };
  });
  
  // On mobile, combine the row headers into a single {rowSpan: 1, colSpan: 1} header.
  if (!showingDesktopUi.value) {
    rows.forEach(row => {
      row.headers = [{
        rowspan: 1,
        colspan: 1,
        text: row.headers.map((header, index) => index ? header.text.toLowerCase() : header.text).join(" ")
      }]
    });
  }

  // Filter out null headers.
  rows = rows.map(row => ({
    ... row,
    headers: row.headers.filter(header => header != null)
  }));

  let rowWithBottomLeftHeader = rows.findLast(row => {
    let headersColspan = 0;
    row.headers.forEach(header => headersColspan += header.colspan);
    return headersColspan == maxHeaderLength;
  });
  if (rowWithBottomLeftHeader) {
    rowWithBottomLeftHeader.headers[0].cssClasses = [
      "bottomLeft"
    ];
  }

  return rows;
});

let maxHeaderLength = computed(() => {
  let headerLengths = rows.value.map(row => sumBy(row.headers, header => header.colspan));
  return Math.max(... headerLengths);
});

let columnSelectionCode = ref("selectedColumn");

let selectedRowId = ref(null);
let selectedRow = computed(() => rows.value.find(row => row.id == selectedRowId.value));

let selectedCellIndex = ref(null);
let selectedCell = computed(() => {
  return selectedRow.value?.cells[selectedCellIndex.value];
});

let selectedCells = computed(() => {
  let row = rows.value.find(row => row.id == selectedRowId.value);
  if (row == null) return [];
  
  let cell = row.cells[selectedCellIndex.value];
  if (cell == null) {
    return null;
  }
  else if (columnSelectionCode.value == "selectedColumn") {
    return [ cell ];
  }
  else if (columnSelectionCode.value == "allColumns") {
    return row.cells.filter(cell => cell != null);
  }
  else {
    let sessions = store.sessions.filter(session => session.system != null);
    let allBins = sessions.flatMap(session => session.system.bins.children);
    let selectedBins = allBins.filter(bin => {
      let system = bin.$session.system;
      return (bin.binGroups.value.includes(columnSelectionCode.value) || system.boardName.value == columnSelectionCode.value);
    });
    return row.cells.filter(cell => {
      let bin = cell?.componentArgs.tag.$parents().find(parent => allBins.includes(parent));
      return selectedBins.includes(bin);
    });
  }
});

// Reset the columns selection when 
//  1) Selecting a column outside of the previous selection, or
//  2) Selecting a cell for which applyToAllColumnsEnabled is false.
watch(selectedCell, () => {
  if (selectedCell.value != null) {
    let selectedCellIndexes = selectedCells.value.map(cell => selectedRow.value.cells.indexOf(cell));
    let shouldResetColumnSelections = (
      !selectedCellIndexes.includes(selectedCellIndex.value) ||
      !cellApplyToAllColumnsEnabled(selectedRow.value, selectedCell.value)
    );
    if (shouldResetColumnSelections)
      columnSelectionCode.value = "selectedColumn";
  }
});

// Prevents triggering unecessary re-renders of all cell components when
// selectedCells changes, by returning null for rows that aren't selected.
function selectedCellsOrNull(row) {
  if (row.id == selectedRowId.value) {
    return selectedCells.value;
  }
  else {
    return null;
  }
}

function toggleShowingAllBins() {
  store.showingAllBinsPreference = !store.showingAllBinsPreference;
}

function cellApplyToAllColumnsEnabled(row, cell) {
  let relevantCells = row.cells.filter(cell => cell != null);
  return (
    relevantCells.length > 1 &&
    cell.tag.applyToAllColumnsEnabled?.value !== false &&
    cell.componentArgs.applyToAllColumnsEnabled !== false
  );
}

function isShowingPopover(row, cellIndex) {
  return (
    row.id == selectedRowId.value && 
    cellIndex == selectedCellIndex.value
  );
}

function setShowingPopover(row, cellIndex, value) {
  if (value) {
    selectedRowId.value = row.id;
    selectedCellIndex.value = cellIndex;
  }
  else if (isShowingPopover(row, cellIndex)) {
    selectedRowId.value = null;
    selectedCellIndex.value = null;
  }
}

// Horizontally position the table body <th> elements so the table can be horizontally scrolled.
// Normally, it is sufficient to simply specify 'position: sticky', but this fails when there are
// <th> elements spanning several rows or columns.
let tableRef = ref();
function setHeaderColumnOffsets() {
  // Early return if we don't have the tableRef yet. This prevents an error occuring on rare occasions when tableRef 
  // doesn't yet have a value. Not sure how this function is being called when that's the case though.
  if (tableRef.value == null)
    return;

  let rows = [ ... (tableRef.value.querySelectorAll("tbody tr") ?? []) ];
  let hdrCells = rows.flatMap(row => [...row.querySelectorAll("th")]);

  // Compute the row and column index for each <th> element.
  let hdrCellsGrid = [];
  for (let i = 0; i < rows.length; i++) hdrCellsGrid.push([]);
  hdrCells.forEach(hdrCell => {
    let rowIndex = rows.findIndex(row => [...row.querySelectorAll("th")].includes(hdrCell));
    hdrCell.rowIndex = rowIndex;
    hdrCell.columnIndex = hdrCellsGrid[rowIndex].length;
    hdrCellsGrid[rowIndex].push(hdrCell);
    for (let i = 0; i < (hdrCell.colSpan - 1); i++) hdrCellsGrid[rowIndex].push(null);          // Fill to the right.
    for (let i = 0; i < (hdrCell.rowSpan - 1); i++) hdrCellsGrid[rowIndex + 1 + i].push(null);  // Fill below.
  });

  // Find the first <th> in each column that has colSpan == 1. Those <th> have the column's width.
  let hdrCellsSingleColSpan = hdrCells.filter(hdrCell => hdrCell.colSpan == 1);
  let hdrCellsWithColumnWidth = [];
  for (let i = 0; i < maxHeaderLength.value; i++) {
    hdrCellsWithColumnWidth.push(hdrCellsSingleColSpan.find(hdrCell => hdrCell.columnIndex == i));
  }

  // Compute each column's distance from table's left edge.
  let columnOffsets = hdrCellsWithColumnWidth.map((hdrCell, index) => {
    let precedingCells = hdrCellsWithColumnWidth.slice(0, index);
    let offset = sumBy(precedingCells, cell => cell.offsetWidth);
    return offset;
  });

  // Set each header cell's .left property according to the corresponding column offset computed above.
  hdrCells.forEach(hdrCell => {
    hdrCell.style.left = `${columnOffsets[hdrCell.columnIndex]}px`;
  });
};

watch([ firstRender, rows ], async () => {
  await promiseTimeout();
  setHeaderColumnOffsets();
});
</script>

<style>
.p-toast-detail {
  margin-top: 5px;
}
</style>

<style scoped>
.MultiColumnTable {
  background: white;
  overscroll-behavior: none; /* Overscroll causes the entire page to slide around too much when there isn't much scroll required. */
  --border-style: 1px solid #888;
  --table-border-bottom-style: 1px solid #888;
}
table {
  width: 100%;
}
th.tableTitle {
  padding-left: 30px;
  padding-right: 30px;
  font-size: large;
  font-weight: bold;
  color: var(--primary-700);
}
th {
  font-weight: 600;
  background: white;
}
th, :deep(td) {
  padding: 10px;
}
tbody th {
  height: 60px;
  text-align: center;
}
:deep(td) {
  text-align: center;
}
:deep(td.showingPopover) {
  background: rgba(30, 143, 255, 0.2);
}
:deep(td.isCellSelected:not(.showingPopover)) {
  background: rgba(30, 143, 255, 0.07);
}
/* Sticky headers */
table {
  border-collapse: separate; /* borders don't stay attached to sticky headers otherwise */
  border-spacing: 0;
}
thead {
  position: sticky;
  top: 0;
  z-index: 1;
}

tbody th {
  position: sticky;
  left: 0;
}
tbody th:last-of-type {
  border-right: var(--border-style);
}
thead th.tableTitle {
  position: sticky;
  top: 0;
  left: 0;
  border-right: var(--border-style);
}
thead th:not(.tableTitle):not(.firstColumnHeader) {
  border-left: var(--border-style);
}
th, :deep(td) {
  border-bottom: var(--border-style);
}
:deep(td):not(:first-of-type) {
  border-left: var(--border-style);
}
tbody tr:last-child th, :deep(tbody) tr:last-child td {
  border-bottom: 0px;
}
/* Top, bottom, side, and corner borders */
thead tr:first-child  th { border-top: var(--border-style); }
tbody th { border-left: var(--border-style); }
:deep(tbody) td:last-child { border-right: var(--border-style); }
tbody tr:last-child th, :deep(tbody) tr:last-child td { border-bottom: var(--table-border-bottom-style); }
thead tr:first-child th:first-child {
  border-top: var(--border-style);
  border-left: var(--border-style);
  border-top-left-radius: 10px;
}
thead tr:first-child th:last-child {
  border-top: var(--border-style);
  border-top-right-radius: 10px;
}
thead tr th:last-child {
  border-right: var(--border-style);
}
tbody th.bottomLeft {
  border-bottom: var(--table-border-bottom-style);
  border-left: var(--border-style);
  border-bottom-left-radius: 10px;
}
:deep(tbody) tr:last-child td:last-child {
  border-bottom: var(--table-border-bottom-style);
  border-right: var(--border-style);
  border-bottom-right-radius: 10px;
}

.desktop th {
  color: #263140;
  white-space: nowrap;
  background: whitesmoke;
}
.desktop tbody th {
  height: initial;
}
.desktop th, .desktop :deep(td) {
  padding: 8px;
  padding-left: 15px;
  padding-right: 15px;
}
.desktop thead th {
  padding: 3px;
}

.mobile .MultiColumnTable::-webkit-scrollbar { display: none; }
.mobile .MultiColumnTable {
  --border-style: 1px solid #cbd5e1;
  --table-border-bottom-style: 1px solid #cbd5e1;
}
.mobile th {
  background: #f9f9fa;
}
.mobile tbody th {
  text-align: left;
}

.mobile th:first-child {
  border-left: 0;
}
.mobile thead tr:first-child th:first-child {
  border-left: 0;
}
.mobile tbody th.bottomLeft {
  border-left: 0;
}

.mobile thead tr:first-child th:first-child {
  border-top-left-radius: 0;
}
.mobile thead tr:first-child th:last-child {
  border-top-right-radius: 0;
}
.mobile tbody th.bottomLeft {
  border-bottom-left-radius: 0;
}
.mobile :deep(tbody) tr:last-child td:last-child {
  border-bottom-right-radius: 0;
}
</style>